/**
 * Pickups: spørsmål som har stått for lenge.
 *
 * Et spørsmål som ikke er stilt 24 timer etter at det ble skrevet hører
 * ikke lenger til dagen man står i. Det angår som regel en tidligere
 * historie som er fulgt opp, og det er noe annet enn det som er aktuelt nå.
 * Da skal det stå for seg, slik at den som synker ser forskjell i ett blikk.
 * Avgjort 26.10.2026.
 *
 * ========  REGELEN LIGGER HER, IKKE I HVER FLATE  ========
 *
 * Appen og nettflata viser den samme lista, og de skal aldri kunne være
 * uenige om hva som er en pickup. To kopier av «24 timer» ville før eller
 * siden blitt til 24 og 48 — det er den slags forskjell ingen oppdager før
 * noen har stilt et spørsmål to ganger.
 *
 * ========  INGEN TILSTAND, BARE EN FØLGE  ========
 *
 * «Pickup» er ikke noe noen setter. Det følger av to ting vi alt vet: når
 * spørsmålet ble skrevet, og om det er stilt. Et felt for det ville vært et
 * tredje sted å holde i takt, og det ville kommet i utakt — akkurat som en
 * `answered`-kolonne ville gjort. Se migrasjon 20261011140000.
 *
 * Det betyr også at et spørsmål går TILBAKE til hovedlista i det noen
 * fjerner haken, og at et spørsmål redaktør nettopp har skrevet aldri er en
 * pickup. Begge deler følger av seg selv.
 *
 * ========  DE GENERELLE BLIR ALDRI PICKUPS  ========
 *
 * Et spørsmål uten deltaker stilles til alle, og det kan stilles hver gang
 * man synker — «Hvordan har du det?» er like aktuelt på dag 12 som på dag
 * 1. Det hører til lista for godt, ikke til dagen det ble skrevet.
 *
 * Alderen betyr altså noe helt annet for dem: et generelt spørsmål som har
 * stått i tre uker er ikke glemt, det er evergreen. Presisert 26.10.2026.
 */

/** Grensen. Ett døgn, fordi en opptaksdag er enheten alt annet måles i. */
export const PICKUP_ETTER_MS = 24 * 60 * 60 * 1000;

/**
 * Er spørsmålet en pickup for denne deltakeren?
 *
 * PARET AVGJØR, ikke spørsmålet alene — som overalt ellers i synken. Et
 * spørsmål som hører til ÉN deltaker og står ustilt til hen et døgn etter,
 * er en pickup for hen.
 *
 * `skrevetMs` er `su_question.created_at`. Er den ukjent — en rad fra før
 * dette fantes — er svaret nei: vi vet ikke når den ble skrevet, og å
 * gjette ville flyttet spørsmål ut av lista på feil grunnlag.
 *
 * `generelt` er `participant_id is null`. De blir aldri pickups; se over.
 *
 * Et objekt og ikke tre løse argumenter: `erPickup(ms, true, false, naa)`
 * er umulig å lese, og to boolske verdier etter hverandre er lett å bytte
 * om på uten at noe sier fra.
 */
export function erPickup(
  spoersmaal: { readonly skrevetMs: number | null; readonly generelt: boolean },
  stilt: boolean,
  naaMs: number,
): boolean {
  if (spoersmaal.generelt || stilt) return false;
  const skrevet = spoersmaal.skrevetMs;
  if (skrevet === null || Number.isNaN(skrevet)) return false;
  return naaMs - skrevet > PICKUP_ETTER_MS;
}
